import React, { Component } from 'react';
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Autocomplete,
    Chip,
    FormControlLabel,
    Checkbox,
    Box
} from '@mui/material';
import { updateFlag, createFlag } from '../../../../services/clinicPortalServices/flagService';
import { getAllAnalytes } from '../../../../services/clinicPortalServices/analyteService';
import toastr from 'toastr';

export default class FlagDetails extends Component {
    constructor(props) {
        super(props);
        this.state = {
            name: '',
            description: '',
            triggeredWhenValue: 'Other',
            colorForConsistent: '000000',
            colorForInconsistent: 'FF0000',
            qualitative: false,
            triggerFormula: '',
            analytes: [],
            outcomeRule: '',
            hl7InterpretationCode: '',
            flagType: 'general',
            analyteOptions: [],
            errors: []
        };
    }

    componentDidMount() {
        this.loadAnalytes();
        if (this.props.flagData && !this.props.isCreating) {
            this.loadFlagDetails();
        }
    }

    loadAnalytes = () => {
        getAllAnalytes()
            .then((res) => {
                if (res.data && res.data) {
                    this.setState({ analyteOptions: res.data });
                }
            })
            .catch((error) => {
                console.error('Error loading analytes:', error);
            });
    };

    loadFlagDetails = () => {
        const flag = this.props.flagData;
        this.setState({
            name: flag.name || '',
            description: flag.description || '',
            triggeredWhenValue: flag.triggeredWhenValue || 'Other',
            colorForConsistent: flag.colorForConsistent || '000000',
            colorForInconsistent: flag.colorForInconsistent || 'FF0000',
            qualitative: flag.qualitative || false,
            triggerFormula: flag.triggerFormula || '',
            analytes: flag.analytes ? flag.analytes.map((a) => a._id) : [],
            outcomeRule: flag.outcomeRule || '',
            hl7InterpretationCode: flag.hl7InterpretationCode || '',
            flagType: flag.flagType || 'general'
        });
    };

    handleChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value
        });
    };

    handleCheckboxChange = (e) => {
        this.setState({
            [e.target.name]: e.target.checked
        });
    };

    hasError = (key) => {
        return this.state.errors.indexOf(key) !== -1;
    };

    validateForm = () => {
        let errors = [];
        if (!this.state.name) errors.push('name');
        if (!this.state.description) errors.push('description');
        if (!this.state.triggerFormula) errors.push('triggerFormula');

        this.setState({ errors: errors });
        return errors.length === 0;
    };

    handleSave = () => {
        if (!this.validateForm()) {
            return;
        }

        const flagData = {
            name: this.state.name,
            description: this.state.description,
            triggeredWhenValue: this.state.triggeredWhenValue,
            colorForConsistent: this.state.colorForConsistent,
            colorForInconsistent: this.state.colorForInconsistent,
            qualitative: this.state.qualitative,
            triggerFormula: this.state.triggerFormula,
            analytes: this.state.analytes,
            outcomeRule: this.state.outcomeRule,
            hl7InterpretationCode: this.state.hl7InterpretationCode,
            flagType: this.state.flagType
        };

        const savePromise = this.props.isCreating
            ? createFlag(flagData)
            : updateFlag(this.props.flagData._id, flagData);

        savePromise
            .then((res) => {
                if (res.data && res.data.success) {
                    toastr.success(this.props.isCreating ? 'Flag created successfully' : 'Flag updated successfully');
                    this.props.handleClose();
                }
            })
            .catch((error) => {
                console.error(this.props.isCreating ? 'Error creating flag:' : 'Error updating flag:', error);
                toastr.error(
                    error.response?.data?.message ||
                        (this.props.isCreating ? 'Failed to create flag' : 'Failed to update flag')
                );
            });
    };

    render() {
        const triggeredWhenOptions = [
            'Less Than Low',
            'Less Than Critical Low',
            'Other',
            'Greater Than High',
            'Greater Than Critical High'
        ];

        const outcomeRules = ['', 'Consistent if Medication Present', 'Consistent if Medication NOT Present'];

        return (
            <div>
                <div className="row">
                    <div className="col-md-6">
                        <TextField
                            fullWidth
                            label="Name *"
                            name="name"
                            value={this.state.name}
                            onChange={this.handleChange}
                            error={this.hasError('name')}
                            helperText={this.hasError('name') ? 'Name is required' : ''}
                            margin="normal"
                        />
                    </div>
                    <div className="col-md-6">
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Flag Type</InputLabel>
                            <Select
                                name="flagType"
                                value={this.state.flagType}
                                onChange={this.handleChange}
                                label="Flag Type"
                            >
                                <MenuItem value="general">General</MenuItem>
                                <MenuItem value="toxicology">Toxicology</MenuItem>
                                <MenuItem value="validity">Validity</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className="col-md-12">
                        <TextField
                            fullWidth
                            label="Description *"
                            name="description"
                            value={this.state.description}
                            onChange={this.handleChange}
                            error={this.hasError('description')}
                            helperText={this.hasError('description') ? 'Description is required' : ''}
                            margin="normal"
                        />
                    </div>
                    <div className="col-md-12">
                        <TextField
                            fullWidth
                            label="Trigger Formula *"
                            name="triggerFormula"
                            value={this.state.triggerFormula}
                            onChange={this.handleChange}
                            error={this.hasError('triggerFormula')}
                            helperText={
                                this.hasError('triggerFormula')
                                    ? 'Trigger formula is required'
                                    : 'Use {value}, {Low}, {High}, {Critical Low}, {Critical High} as placeholders'
                            }
                            margin="normal"
                        />
                    </div>
                    <div className="col-md-6">
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Triggered When Value</InputLabel>
                            <Select
                                name="triggeredWhenValue"
                                value={this.state.triggeredWhenValue}
                                onChange={this.handleChange}
                                label="Triggered When Value"
                            >
                                {triggeredWhenOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                    <div className="col-md-6">
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Outcome Rule</InputLabel>
                            <Select
                                name="outcomeRule"
                                value={this.state.outcomeRule}
                                onChange={this.handleChange}
                                label="Outcome Rule"
                            >
                                {outcomeRules.map((rule) => (
                                    <MenuItem key={rule} value={rule}>
                                        {rule || '(None)'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                    <div className="col-md-4">
                        <TextField
                            fullWidth
                            label="Color for Consistent"
                            name="colorForConsistent"
                            value={this.state.colorForConsistent}
                            onChange={this.handleChange}
                            margin="normal"
                            helperText="Hex color without #"
                            InputProps={{
                                startAdornment: (
                                    <Box
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            backgroundColor: `#${this.state.colorForConsistent}`,
                                            border: '1px solid #ccc',
                                            borderRadius: 1,
                                            mr: 1
                                        }}
                                    />
                                )
                            }}
                        />
                    </div>
                    <div className="col-md-4">
                        <TextField
                            fullWidth
                            label="Color for Inconsistent"
                            name="colorForInconsistent"
                            value={this.state.colorForInconsistent}
                            onChange={this.handleChange}
                            margin="normal"
                            helperText="Hex color without #"
                            InputProps={{
                                startAdornment: (
                                    <Box
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            backgroundColor: `#${this.state.colorForInconsistent}`,
                                            border: '1px solid #ccc',
                                            borderRadius: 1,
                                            mr: 1
                                        }}
                                    />
                                )
                            }}
                        />
                    </div>
                    <div className="col-md-4">
                        <TextField
                            fullWidth
                            label="HL7 Code"
                            name="hl7InterpretationCode"
                            value={this.state.hl7InterpretationCode}
                            onChange={this.handleChange}
                            margin="normal"
                        />
                    </div>
                    <div className="col-md-12">
                        <Autocomplete
                            multiple
                            options={this.state.analyteOptions}
                            getOptionLabel={(option) => option.name || ''}
                            value={this.state.analyteOptions.filter((analyte) =>
                                this.state.analytes.includes(analyte._id)
                            )}
                            onChange={(event, newValue) => {
                                this.setState({
                                    analytes: newValue.map((analyte) => analyte._id)
                                });
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        variant="outlined"
                                        label={option.name}
                                        {...getTagProps({ index })}
                                        key={option._id}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Analytes"
                                    placeholder="Select analytes (leave empty for all)"
                                    margin="normal"
                                />
                            )}
                        />
                    </div>
                    <div className="col-md-12">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={this.state.qualitative}
                                    onChange={this.handleCheckboxChange}
                                    name="qualitative"
                                />
                            }
                            label="Qualitative Flag"
                        />
                    </div>
                </div>
                <div className="row mt-4">
                    <div className="col-12 text-right">
                        <button className="btn btn-secondary mr-2" onClick={this.props.handleClose}>
                            Cancel
                        </button>
                        <button className="btn btn-primary" onClick={this.handleSave}>
                            {this.props.isCreating ? 'Create Flag' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
